/**
 * G2eGente - Formulário de Engenharia de Cargo
 * Criar/Editar descrição de cargo baseado no template DOCX do usuário
 * Inclui: Questionário DISC e Perfil Ideal do Cargo
 * Inclui: Importação de PDF/DOCX e Geração por IA
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { jobPositionsAPI, discAPI, evaluationsAPI, jobPositionsImportAPI, trainingsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft, 
  Save, 
  Loader2,
  Plus,
  Trash2,
  FileText,
  GraduationCap,
  Clock,
  Target,
  Brain,
  Heart,
  Lightbulb,
  Crown,
  Award,
  Star,
  ChevronRight,
  ChevronLeft,
  ClipboardList,
  Upload,
  Sparkles,
  FileUp,
  Wand2,
  Search,
  X,
  AlertTriangle,
  ListChecks,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const ESCOLARIDADE_OPTIONS = [
  'Ensino Fundamental',
  'Ensino Médio',
  'Técnico',
  'Superior Incompleto',
  'Superior Completo',
  'Pós-Graduação',
  'MBA',
  'Mestrado',
  'Doutorado'
];

const DISC_OPTIONS = ['D', 'I', 'S', 'C', 'DI', 'DC', 'IS', 'IC', 'SC', 'SI'];

// Icons for evaluation levels
const getNivelIcon = (nivel) => {
  const icons = {
    'entrega': Target,
    'comportamento': Heart,
    'cognitivo': Brain,
    'aspiracao': Lightbulb,
    'experiencia': Award,
    'lideranca': Crown
  };
  return icons[nivel] || Target;
};

// DISC colors
const DISC_COLORS = {
  'D': { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', name: 'Dominância' },
  'I': { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300', name: 'Influência' },
  'S': { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', name: 'Estabilidade' },
  'C': { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', name: 'Conformidade' }
};

export default function JobPositionForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = !!id;
  const perfilCargoRef = useRef(null);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [highlightPerfil, setHighlightPerfil] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  
  // DISC Questionnaire state
  const [discQuestionnaire, setDiscQuestionnaire] = useState(null);
  const [loadingQuestionnaire, setLoadingQuestionnaire] = useState(false);
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState({});
  const [questionnaireStep, setQuestionnaireStep] = useState(0);
  const [calculatedProfile, setCalculatedProfile] = useState(null);
  const [questionnaireMode, setQuestionnaireMode] = useState('simple');
  
  // Evaluation template
  const [evaluationTemplate, setEvaluationTemplate] = useState(null);
  
  // Form state based on DOCX template structure
  const [form, setForm] = useState({
    // Identificação
    titulo: '',
    codigo_cargo: '',
    codigo_cbo: '',
    jornada_diaria: '08:00',
    jornada_semanal: '44h',
    
    // Descrição Sumária (Missão do Cargo)
    descricao_sumaria: '',
    
    // Responsabilidades (Principais Responsabilidades)
    responsabilidades: [
      { categoria: 'Gestão', itens: [''] }
    ],
    
    // Pré-requisitos
    experiencia: '',
    escolaridade: '',
    escolaridade_desejavel: '',
    informatica: '',
    informatica_avancado: '',
    conhecimentos_especificos: [''],
    treinamentos_corporativos: [''],
    requisitos_legais: [''],
    
    // Habilidades e Competências
    habilidades_competencias: [''],
    
    // Linha de Carreira
    linha_carreira: '',
    
    // Perfil Ideal do Cargo (DISC + Níveis de Avaliação)
    perfil_cargo: {
      disc_desejado: '',
      disc_percentages: null,
      nivel_experiencia: '',
      descricao_perfil: '',
      niveis: [] // Níveis de avaliação com pontuações ideais
    }
  });

  // Import/AI states
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [aiLoading, setAILoading] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importForm, setImportForm] = useState(null); // Editable import form state
  const [aiForm, setAIForm] = useState({
    titulo: '',
    codigo_cbo: '',
    setor: '',
    nivel_hierarquico: '',
    descricao_previa: '' // Campo de descrição prévia para auxiliar IA
  });
  const fileInputRef = useRef(null);

  // Treinamentos do banco de dados
  const [availableTrainings, setAvailableTrainings] = useState([]);
  const [trainingsSearch, setTrainingsSearch] = useState('');
  const [showTrainingsDropdown, setShowTrainingsDropdown] = useState(false);

  useEffect(() => {
    // Load evaluation template
    const loadTemplate = async () => {
      try {
        const res = await evaluationsAPI.getTemplate();
        setEvaluationTemplate(res.data);
      } catch (err) {
        console.error('Error loading evaluation template:', err);
      }
    };
    loadTemplate();
    
    // Load available trainings
    const loadTrainings = async () => {
      try {
        const res = await trainingsAPI.list();
        setAvailableTrainings(res.data || []);
      } catch (err) {
        console.error('Error loading trainings:', err);
      }
    };
    loadTrainings();
    
    if (isEdit) {
      fetchPosition();
    }
  }, [id]);

  // Scroll to Perfil do Cargo section if coming from "Perfil Ideal" button
  useEffect(() => {
    if (location.state?.focusSection === 'perfil' && perfilCargoRef.current && !loading) {
      setHighlightPerfil(true);
      setTimeout(() => {
        perfilCargoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      // Remove highlight after animation
      setTimeout(() => setHighlightPerfil(false), 3000);
    }
  }, [location.state, loading]);

  const fetchPosition = async () => {
    try {
      const res = await jobPositionsAPI.getById(id);
      const data = res.data;
      
      // Ensure arrays have at least one empty item for UI
      if (!data.responsabilidades?.length) {
        data.responsabilidades = [{ categoria: 'Gestão', itens: [''] }];
      }
      if (!data.conhecimentos_especificos?.length) {
        data.conhecimentos_especificos = [''];
      }
      // Convert treinamentos_corporativos from string to array if needed
      if (!Array.isArray(data.treinamentos_corporativos)) {
        data.treinamentos_corporativos = data.treinamentos_corporativos 
          ? [data.treinamentos_corporativos] 
          : [''];
      } else if (!data.treinamentos_corporativos.length) {
        data.treinamentos_corporativos = [''];
      }
      // Convert requisitos_legais from string to array if needed
      if (!Array.isArray(data.requisitos_legais)) {
        data.requisitos_legais = data.requisitos_legais 
          ? [data.requisitos_legais] 
          : [''];
      } else if (!data.requisitos_legais.length) {
        data.requisitos_legais = [''];
      }
      if (!data.habilidades_competencias?.length) {
        data.habilidades_competencias = [''];
      }
      if (!data.perfil_cargo) {
        data.perfil_cargo = { disc_desejado: '', nivel_experiencia: '', descricao_perfil: '' };
      }
      
      setForm(data);
    } catch (error) {
      toast.error('Erro ao carregar cargo');
      navigate('/company/job-positions');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handlePerfilChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      perfil_cargo: { ...prev.perfil_cargo, [field]: value }
    }));
  };

  // Array field helpers
  const addArrayItem = (field) => {
    setForm(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const updateArrayItem = (field, index, value) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const removeArrayItem = (field, index) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  // Responsabilidades helpers
  const addResponsabilidade = () => {
    setForm(prev => ({
      ...prev,
      responsabilidades: [...prev.responsabilidades, { categoria: '', itens: [''] }]
    }));
  };

  const updateResponsabilidadeCategoria = (index, value) => {
    setForm(prev => ({
      ...prev,
      responsabilidades: prev.responsabilidades.map((r, i) => 
        i === index ? { ...r, categoria: value } : r
      )
    }));
  };

  const addResponsabilidadeItem = (respIndex) => {
    setForm(prev => ({
      ...prev,
      responsabilidades: prev.responsabilidades.map((r, i) => 
        i === respIndex ? { ...r, itens: [...r.itens, ''] } : r
      )
    }));
  };

  const updateResponsabilidadeItem = (respIndex, itemIndex, value) => {
    setForm(prev => ({
      ...prev,
      responsabilidades: prev.responsabilidades.map((r, i) => 
        i === respIndex 
          ? { ...r, itens: r.itens.map((item, j) => j === itemIndex ? value : item) }
          : r
      )
    }));
  };

  const removeResponsabilidadeItem = (respIndex, itemIndex) => {
    setForm(prev => ({
      ...prev,
      responsabilidades: prev.responsabilidades.map((r, i) => 
        i === respIndex 
          ? { ...r, itens: r.itens.filter((_, j) => j !== itemIndex) }
          : r
      )
    }));
  };

  const removeResponsabilidade = (index) => {
    setForm(prev => ({
      ...prev,
      responsabilidades: prev.responsabilidades.filter((_, i) => i !== index)
    }));
  };

  // ========== DISC QUESTIONNAIRE FUNCTIONS ==========
  
  // Initialize perfil_cargo with evaluation levels
  const initializePerfilCargo = (template) => {
    if (!template?.niveis) return form.perfil_cargo;
    
    return {
      ...form.perfil_cargo,
      niveis: template.niveis.map(nivel => ({
        ...nivel,
        steps: nivel.steps.map(step => ({
          ...step,
          pontuacao_ideal: 3 // Default to 3 stars
        }))
      }))
    };
  };

  // Start DISC questionnaire
  const handleStartQuestionnaire = async () => {
    setLoadingQuestionnaire(true);
    try {
      const res = await discAPI.getQuestionnaire(form.perfil_cargo?.nivel_experiencia || 'pleno');
      setDiscQuestionnaire(res.data);
      setQuestionnaireStep(1);
    } catch (error) {
      toast.error('Erro ao carregar questionário DISC');
    } finally {
      setLoadingQuestionnaire(false);
    }
  };

  // Finish questionnaire and calculate profile
  const handleFinishQuestionnaire = async () => {
    try {
      const respostas = Object.entries(questionnaireAnswers).map(([pergunta, resposta]) => ({
        pergunta: parseInt(pergunta),
        resposta
      }));
      
      const res = await discAPI.calculateProfile(respostas);
      setCalculatedProfile(res.data);
      
      // Update form with calculated profile
      setForm(prev => ({
        ...prev,
        perfil_cargo: {
          ...prev.perfil_cargo,
          disc_desejado: res.data.perfil_predominante,
          disc_percentages: res.data.percentuais
        }
      }));
      
      setQuestionnaireStep(26);
      toast.success('Perfil DISC calculado com sucesso!');
    } catch (error) {
      toast.error('Erro ao calcular perfil DISC');
    }
  };

  // Get all questions as a flat array from the questionnaire
  const getAllQuestions = () => {
    if (!discQuestionnaire?.questionario) return [];
    const q = discQuestionnaire.questionario;
    // Combine base, nivel, and comportamental questions
    const baseQuestions = (q.base || []).map(item => ({
      texto: item.pergunta || item.texto,
      opcoes: item.opcoes?.map(opt => ({
        texto: opt.texto,
        perfil: opt.disc || opt.perfil || 'D'
      })) || []
    }));
    const nivelQuestions = (q.nivel || []).map(item => ({
      texto: item.pergunta || item.texto,
      opcoes: item.opcoes?.map(opt => ({
        texto: opt.texto,
        perfil: opt.disc || opt.perfil || 'D'
      })) || []
    }));
    const comportamentalQuestions = (q.comportamental || []).map(item => ({
      texto: item.pergunta || item.texto,
      opcoes: item.opcoes?.map(opt => ({
        texto: opt.texto,
        perfil: opt.disc || opt.perfil || 'D'
      })) || []
    }));
    return [...baseQuestions, ...nivelQuestions, ...comportamentalQuestions];
  };

  // Get current question from questionnaire
  const getCurrentQuestion = () => {
    const allQuestions = getAllQuestions();
    if (allQuestions.length === 0) return null;
    return allQuestions[questionnaireStep - 1] || null;
  };

  const totalQuestions = discQuestionnaire?.total_perguntas || 25;

  // Update step score in evaluation levels
  const updateStepScore = (nivelIndex, stepIndex, score) => {
    setForm(prev => ({
      ...prev,
      perfil_cargo: {
        ...prev.perfil_cargo,
        niveis: prev.perfil_cargo.niveis.map((nivel, ni) => 
          ni === nivelIndex 
            ? {
                ...nivel,
                steps: nivel.steps.map((step, si) => 
                  si === stepIndex ? { ...step, pontuacao_ideal: score } : step
                )
              }
            : nivel
        )
      }
    }));
  };

  // Calculate average for a nivel
  const calculateNivelMedia = (nivel) => {
    if (!nivel?.steps?.length) return 0;
    const total = nivel.steps.reduce((sum, step) => sum + (step.pontuacao_ideal || 0), 0);
    return (total / nivel.steps.length).toFixed(1);
  };

  // Calculate overall average
  const calculateOverallMedia = () => {
    const niveis = form.perfil_cargo?.niveis || [];
    if (!niveis.length) return 0;
    
    let totalScore = 0;
    let totalSteps = 0;
    
    niveis.forEach(nivel => {
      nivel.steps?.forEach(step => {
        totalScore += step.pontuacao_ideal || 0;
        totalSteps++;
      });
    });
    
    return totalSteps > 0 ? (totalScore / totalSteps).toFixed(1) : 0;
  };

  // Initialize niveis when template loads
  useEffect(() => {
    if (evaluationTemplate && !form.perfil_cargo?.niveis?.length) {
      setForm(prev => ({
        ...prev,
        perfil_cargo: initializePerfilCargo(evaluationTemplate)
      }));
    }
  }, [evaluationTemplate]);

  // ========== END DISC FUNCTIONS ==========

  // ========== IMPORT/AI FUNCTIONS ==========
  
  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword'
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de arquivo não suportado. Use PDF ou DOCX.');
      return;
    }

    setImportLoading(true);
    try {
      const res = await jobPositionsImportAPI.importDocument(file);
      
      if (res.data.success) {
        const extractedData = res.data.extracted_data;
        setImportPreview(extractedData);
        // Initialize editable import form with extracted data
        setImportForm({
          titulo: extractedData.titulo || '',
          codigo_cargo: extractedData.codigo_cargo || '',
          codigo_cbo: extractedData.codigo_cbo || '',
          jornada_diaria: extractedData.jornada_diaria || '',
          jornada_semanal: extractedData.jornada_semanal || '',
          descricao_sumaria: extractedData.descricao_sumaria || '',
          experiencia: extractedData.experiencia || '',
          escolaridade: extractedData.escolaridade || '',
          escolaridade_desejavel: extractedData.escolaridade_desejavel || '',
          informatica: extractedData.informatica || '',
          informatica_avancado: extractedData.informatica_avancado || '',
          requisitos_legais: Array.isArray(extractedData.requisitos_legais) 
            ? extractedData.requisitos_legais 
            : (extractedData.requisitos_legais ? [extractedData.requisitos_legais] : []),
          linha_carreira: extractedData.linha_carreira || '',
          responsabilidades: extractedData.responsabilidades || [],
          conhecimentos_especificos: extractedData.conhecimentos_especificos || [],
          treinamentos_corporativos: extractedData.treinamentos_corporativos || [],
          habilidades_competencias: extractedData.habilidades_competencias || []
        });
        setShowImportModal(true);
      } else {
        toast.error(res.data.error || 'Erro ao processar documento');
        // Show raw text for manual copy
        if (res.data.raw_text) {
          setImportPreview({ raw_text: res.data.raw_text });
          setImportForm(null);
          setShowImportModal(true);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar documento');
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Check which required fields are missing in import form
  const getImportMissingFields = () => {
    if (!importForm) return [];
    const missing = [];
    if (!importForm.titulo?.trim()) missing.push('titulo');
    if (!importForm.descricao_sumaria?.trim()) missing.push('descricao_sumaria');
    return missing;
  };

  const applyImportedData = () => {
    if (!importForm) return;
    
    // Check required fields
    const missingFields = getImportMissingFields();
    if (missingFields.length > 0) {
      toast.error('Preencha todos os campos obrigatórios antes de aplicar');
      return;
    }

    // Map import form data to main form fields
    const newForm = { ...form };
    
    if (importForm.titulo) newForm.titulo = importForm.titulo;
    if (importForm.codigo_cargo) newForm.codigo_cargo = importForm.codigo_cargo;
    if (importForm.codigo_cbo) newForm.codigo_cbo = importForm.codigo_cbo;
    if (importForm.jornada_diaria) newForm.jornada_diaria = importForm.jornada_diaria;
    if (importForm.jornada_semanal) newForm.jornada_semanal = importForm.jornada_semanal;
    if (importForm.descricao_sumaria) newForm.descricao_sumaria = importForm.descricao_sumaria;
    if (importForm.experiencia) newForm.experiencia = importForm.experiencia;
    if (importForm.escolaridade) newForm.escolaridade = importForm.escolaridade;
    if (importForm.escolaridade_desejavel) newForm.escolaridade_desejavel = importForm.escolaridade_desejavel;
    if (importForm.informatica) newForm.informatica = importForm.informatica;
    if (importForm.informatica_avancado) newForm.informatica_avancado = importForm.informatica_avancado;
    if (importForm.requisitos_legais?.length) {
      newForm.requisitos_legais = importForm.requisitos_legais;
    }
    if (importForm.linha_carreira) newForm.linha_carreira = importForm.linha_carreira;
    
    if (importForm.responsabilidades?.length) {
      newForm.responsabilidades = importForm.responsabilidades;
    }
    if (importForm.conhecimentos_especificos?.length) {
      newForm.conhecimentos_especificos = importForm.conhecimentos_especificos;
    }
    if (importForm.treinamentos_corporativos?.length) {
      newForm.treinamentos_corporativos = importForm.treinamentos_corporativos;
    }
    if (importForm.habilidades_competencias?.length) {
      newForm.habilidades_competencias = importForm.habilidades_competencias;
    }

    setForm(newForm);
    setShowImportModal(false);
    setImportPreview(null);
    setImportForm(null);
    toast.success('Dados importados com sucesso');
  };

  const handleAIGenerate = async () => {
    if (!aiForm.titulo.trim()) {
      toast.error('Informe o título do cargo');
      return;
    }

    setAILoading(true);
    try {
      const res = await jobPositionsImportAPI.generateWithAI(
        aiForm.titulo,
        aiForm.codigo_cbo,
        aiForm.setor,
        aiForm.nivel_hierarquico,
        aiForm.descricao_previa
      );

      if (res.data.success) {
        const generatedData = res.data.generated_data;
        setImportPreview(generatedData);
        // Initialize editable import form with generated data
        setImportForm({
          titulo: generatedData.titulo || aiForm.titulo || '',
          codigo_cargo: generatedData.codigo_cargo || '',
          codigo_cbo: generatedData.codigo_cbo || aiForm.codigo_cbo || '',
          jornada_diaria: generatedData.jornada_diaria || '',
          jornada_semanal: generatedData.jornada_semanal || '',
          descricao_sumaria: generatedData.descricao_sumaria || '',
          experiencia: generatedData.experiencia || '',
          escolaridade: generatedData.escolaridade || '',
          escolaridade_desejavel: generatedData.escolaridade_desejavel || '',
          informatica: generatedData.informatica || '',
          informatica_avancado: generatedData.informatica_avancado || '',
          requisitos_legais: Array.isArray(generatedData.requisitos_legais) 
            ? generatedData.requisitos_legais 
            : (generatedData.requisitos_legais ? [generatedData.requisitos_legais] : []),
          linha_carreira: generatedData.linha_carreira || '',
          responsabilidades: generatedData.responsabilidades || [],
          conhecimentos_especificos: generatedData.conhecimentos_especificos || [],
          treinamentos_corporativos: generatedData.treinamentos_corporativos || [],
          habilidades_competencias: generatedData.habilidades_competencias || []
        });
        setShowAIModal(false);
        setShowImportModal(true);
      } else {
        toast.error('Erro ao gerar descrição');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro na geração por IA');
    } finally {
      setAILoading(false);
    }
  };

  // ========== END IMPORT/AI FUNCTIONS ==========

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.titulo.trim()) {
      toast.error('O título do cargo é obrigatório');
      return;
    }

    setSaving(true);
    try {
      // Ensure treinamentos_corporativos is an array before filtering
      const treinamentosArray = Array.isArray(form.treinamentos_corporativos) 
        ? form.treinamentos_corporativos 
        : (form.treinamentos_corporativos ? [form.treinamentos_corporativos] : []);
      
      // Ensure requisitos_legais is an array before filtering
      const requisitosLegaisArray = Array.isArray(form.requisitos_legais) 
        ? form.requisitos_legais 
        : (form.requisitos_legais ? [form.requisitos_legais] : []);
      
      // Clean up empty items from arrays
      const cleanedForm = {
        ...form,
        conhecimentos_especificos: (form.conhecimentos_especificos || []).filter(k => k && k.trim()),
        treinamentos_corporativos: treinamentosArray.filter(t => t && t.trim()),
        requisitos_legais: requisitosLegaisArray.filter(r => r && r.trim()),
        habilidades_competencias: (form.habilidades_competencias || []).filter(h => h && h.trim()),
        responsabilidades: (form.responsabilidades || [])
          .map(r => ({
            ...r,
            itens: (r.itens || []).filter(i => i && i.trim())
          }))
          .filter(r => r.categoria && r.categoria.trim() && r.itens.length > 0)
      };

      if (isEdit) {
        await jobPositionsAPI.update(id, cleanedForm);
        toast.success('Cargo atualizado com sucesso');
      } else {
        await jobPositionsAPI.create(cleanedForm);
        toast.success('Cargo criado com sucesso');
      }
      
      // Use window.location for more reliable navigation
      window.location.href = '/company/job-positions';
    } catch (error) {
      console.error('Erro ao salvar cargo:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar cargo');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="job-position-form">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Button 
              type="button" 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/company/job-positions')}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {isEdit ? 'Editar Cargo' : 'Novo Cargo'}
              </h1>
              <p className="text-slate-600">Preencha a descrição do cargo</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isEdit && (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileImport}
                  accept=".pdf,.docx,.doc"
                  className="hidden"
                />
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importLoading}
                  data-testid="import-document-btn"
                >
                  {importLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <FileUp className="w-4 h-4 mr-2" />
                  )}
                  Importar
                </Button>
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => setShowAIModal(true)}
                  className="text-purple-600 border-purple-200 hover:bg-purple-50"
                  data-testid="generate-ai-btn"
                >
                  <Wand2 className="w-4 h-4 mr-2" />
                  Gerar com IA
                </Button>
              </>
            )}
            <Button 
              type="submit" 
              className="bg-cyan-600 hover:bg-cyan-700"
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salvar
            </Button>
          </div>
        </div>

        {/* Identificação */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-cyan-600" />
              Identificação do Cargo
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="titulo">Título do Cargo *</Label>
              <Input
                id="titulo"
                value={form.titulo}
                onChange={(e) => handleChange('titulo', e.target.value)}
                placeholder="Ex: Gerente Comercial"
                required
              />
            </div>
            <div>
              <Label htmlFor="codigo_cargo">Código do Cargo</Label>
              <Input
                id="codigo_cargo"
                value={form.codigo_cargo}
                onChange={(e) => handleChange('codigo_cargo', e.target.value)}
                placeholder="Ex: GC-001"
              />
            </div>
            <div>
              <Label htmlFor="codigo_cbo">Código CBO</Label>
              <Input
                id="codigo_cbo"
                value={form.codigo_cbo}
                onChange={(e) => handleChange('codigo_cbo', e.target.value)}
                placeholder="Ex: 1423-05"
              />
            </div>
            <div>
              <Label htmlFor="jornada_diaria">Jornada Diária</Label>
              <Input
                id="jornada_diaria"
                value={form.jornada_diaria}
                onChange={(e) => handleChange('jornada_diaria', e.target.value)}
                placeholder="Ex: 08:00"
              />
            </div>
            <div>
              <Label htmlFor="jornada_semanal">Jornada Semanal</Label>
              <Input
                id="jornada_semanal"
                value={form.jornada_semanal}
                onChange={(e) => handleChange('jornada_semanal', e.target.value)}
                placeholder="Ex: 44h"
              />
            </div>
          </CardContent>
        </Card>

        {/* Missão do Cargo */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-cyan-600" />
              Missão do Cargo
            </CardTitle>
            <CardDescription>Descrição sumária das responsabilidades principais</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={form.descricao_sumaria}
              onChange={(e) => handleChange('descricao_sumaria', e.target.value)}
              placeholder="Descreva a missão principal do cargo..."
              rows={4}
            />
          </CardContent>
        </Card>

        {/* Principais Responsabilidades */}
        <Card>
          <CardHeader>
            <CardTitle>Principais Responsabilidades</CardTitle>
            <CardDescription>Organize as responsabilidades por categoria</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {form.responsabilidades.map((resp, respIndex) => (
              <div key={respIndex} className="border rounded-lg p-4 bg-slate-50">
                <div className="flex items-center gap-2 mb-3">
                  <Input
                    value={resp.categoria}
                    onChange={(e) => updateResponsabilidadeCategoria(respIndex, e.target.value)}
                    placeholder="Nome da categoria (ex: Gestão de Equipe)"
                    className="flex-1 font-medium"
                  />
                  {form.responsabilidades.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeResponsabilidade(respIndex)}
                      className="text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                
                <div className="space-y-2 ml-4">
                  {resp.itens.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-center gap-2">
                      <span className="text-slate-400">•</span>
                      <Input
                        value={item}
                        onChange={(e) => updateResponsabilidadeItem(respIndex, itemIndex, e.target.value)}
                        placeholder="Descreva a responsabilidade..."
                        className="flex-1"
                      />
                      {resp.itens.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeResponsabilidadeItem(respIndex, itemIndex)}
                          className="text-slate-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => addResponsabilidadeItem(respIndex)}
                    className="text-cyan-600"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar item
                  </Button>
                </div>
              </div>
            ))}
            
            <Button
              type="button"
              variant="outline"
              onClick={addResponsabilidade}
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Categoria
            </Button>
          </CardContent>
        </Card>

        {/* Pré-requisitos */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-cyan-600" />
              Pré-requisitos
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Escolaridade Mínima</Label>
              <Select value={form.escolaridade} onValueChange={(v) => handleChange('escolaridade', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {ESCOLARIDADE_OPTIONS.map(opt => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Escolaridade Desejável</Label>
              <Select value={form.escolaridade_desejavel} onValueChange={(v) => handleChange('escolaridade_desejavel', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {ESCOLARIDADE_OPTIONS.map(opt => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="experiencia">Experiência Requerida</Label>
              <Input
                id="experiencia"
                value={form.experiencia}
                onChange={(e) => handleChange('experiencia', e.target.value)}
                placeholder="Ex: Mínimo 3 anos em cargos de liderança comercial"
              />
            </div>
            <div>
              <Label htmlFor="informatica">Informática Básica</Label>
              <Input
                id="informatica"
                value={form.informatica}
                onChange={(e) => handleChange('informatica', e.target.value)}
                placeholder="Ex: Pacote Office, Internet"
              />
            </div>
            <div>
              <Label htmlFor="informatica_avancado">Informática Avançada</Label>
              <Input
                id="informatica_avancado"
                value={form.informatica_avancado}
                onChange={(e) => handleChange('informatica_avancado', e.target.value)}
                placeholder="Ex: CRM, ERP, BI"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Conhecimentos Específicos</Label>
              <div className="space-y-2">
                {form.conhecimentos_especificos.map((conhecimento, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={conhecimento}
                      onChange={(e) => updateArrayItem('conhecimentos_especificos', index, e.target.value)}
                      placeholder="Ex: Gestão de equipes de vendas"
                    />
                    {form.conhecimentos_especificos.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeArrayItem('conhecimentos_especificos', index)}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => addArrayItem('conhecimentos_especificos')}
                  className="text-cyan-600"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar conhecimento
                </Button>
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label>Treinamentos Corporativos (Obrigatórios e Eventuais)</Label>
              <p className="text-xs text-slate-500 mb-2">Selecione os treinamentos cadastrados ou digite para buscar</p>
              <div className="space-y-2">
                {/* Treinamentos selecionados */}
                <div className="flex flex-wrap gap-2 min-h-[32px]">
                  {form.treinamentos_corporativos.filter(t => t).map((treinamento, index) => (
                    <Badge key={index} className="bg-amber-100 text-amber-700 flex items-center gap-1 px-3 py-1">
                      {treinamento}
                      <button
                        type="button"
                        onClick={() => {
                          const newList = form.treinamentos_corporativos.filter((_, i) => i !== index);
                          setForm(prev => ({ ...prev, treinamentos_corporativos: newList.length ? newList : [''] }));
                        }}
                        className="ml-1 hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                
                {/* Busca de treinamentos */}
                <div className="relative">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={trainingsSearch}
                        onChange={(e) => {
                          setTrainingsSearch(e.target.value);
                          setShowTrainingsDropdown(true);
                        }}
                        onFocus={() => setShowTrainingsDropdown(true)}
                        placeholder="Buscar treinamento cadastrado..."
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  {/* Dropdown de treinamentos */}
                  {showTrainingsDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {availableTrainings
                        .filter(t => 
                          t.nome?.toLowerCase().includes(trainingsSearch.toLowerCase()) &&
                          !form.treinamentos_corporativos.includes(t.nome)
                        )
                        .slice(0, 10)
                        .map((training) => (
                          <button
                            key={training.id}
                            type="button"
                            onClick={() => {
                              const currentList = form.treinamentos_corporativos.filter(t => t);
                              if (!currentList.includes(training.nome)) {
                                setForm(prev => ({
                                  ...prev,
                                  treinamentos_corporativos: [...currentList, training.nome]
                                }));
                              }
                              setTrainingsSearch('');
                              setShowTrainingsDropdown(false);
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-slate-100 flex items-center justify-between text-sm"
                          >
                            <span>{training.nome}</span>
                            {training.categoria && (
                              <Badge variant="outline" className="text-xs">{training.categoria}</Badge>
                            )}
                          </button>
                        ))}
                      {availableTrainings.filter(t => 
                        t.nome?.toLowerCase().includes(trainingsSearch.toLowerCase()) &&
                        !form.treinamentos_corporativos.includes(t.nome)
                      ).length === 0 && (
                        <div className="px-4 py-3 text-sm text-slate-500">
                          {trainingsSearch ? (
                            <div className="space-y-2">
                              <p>Nenhum treinamento encontrado.</p>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const currentList = form.treinamentos_corporativos.filter(t => t);
                                  setForm(prev => ({
                                    ...prev,
                                    treinamentos_corporativos: [...currentList, trainingsSearch]
                                  }));
                                  setTrainingsSearch('');
                                  setShowTrainingsDropdown(false);
                                }}
                                className="w-full"
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Adicionar &quot;{trainingsSearch}&quot; manualmente
                              </Button>
                            </div>
                          ) : (
                            <p>Digite para buscar ou cadastre treinamentos em Gestão de Pessoas &gt; Treinamentos</p>
                          )}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => setShowTrainingsDropdown(false)}
                        className="w-full px-4 py-2 text-xs text-slate-400 hover:bg-slate-50 border-t"
                      >
                        Fechar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label>Requisitos Legais</Label>
              <div className="space-y-2">
                {form.requisitos_legais.map((requisito, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={requisito}
                      onChange={(e) => updateArrayItem('requisitos_legais', index, e.target.value)}
                      placeholder="Ex: CNH categoria B, NR-10, NR-35"
                    />
                    {form.requisitos_legais.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeArrayItem('requisitos_legais', index)}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => addArrayItem('requisitos_legais')}
                  className="text-cyan-600"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar requisito legal
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Habilidades e Competências */}
        <Card>
          <CardHeader>
            <CardTitle>Habilidades e Competências</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {form.habilidades_competencias.map((habilidade, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={habilidade}
                    onChange={(e) => updateArrayItem('habilidades_competencias', index, e.target.value)}
                    placeholder="Ex: Liderança, Negociação, Comunicação"
                  />
                  {form.habilidades_competencias.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeArrayItem('habilidades_competencias', index)}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => addArrayItem('habilidades_competencias')}
                className="text-cyan-600"
              >
                <Plus className="w-4 h-4 mr-1" />
                Adicionar habilidade
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Linha de Carreira */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-cyan-600" />
              Linha de Carreira
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={form.linha_carreira}
              onChange={(e) => handleChange('linha_carreira', e.target.value)}
              placeholder="Descreva a possível trajetória de carreira para este cargo..."
              rows={3}
            />
          </CardContent>
        </Card>

        {/* Perfil Ideal do Cargo (DISC + Níveis de Avaliação) */}
        <Card 
          ref={perfilCargoRef}
          className={`transition-all duration-500 ${
            highlightPerfil 
              ? 'ring-4 ring-purple-400 ring-offset-2 shadow-lg shadow-purple-200' 
              : ''
          }`}
        >
          <CardHeader className={highlightPerfil ? 'bg-purple-50' : ''}>
            <CardTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-600" />
              Perfil Ideal do Cargo
              {highlightPerfil && (
                <Badge className="bg-purple-600 text-white animate-pulse ml-2">
                  Edite aqui
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Define o perfil comportamental e os critérios de avaliação ideais para este cargo.
              Esse perfil será usado como parâmetro de contratação.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Mode Selection */}
            <div className="flex gap-4 flex-wrap">
              <Button
                type="button"
                variant={questionnaireMode === 'simple' ? 'default' : 'outline'}
                onClick={() => setQuestionnaireMode('simple')}
                className={questionnaireMode === 'simple' ? 'bg-purple-600' : ''}
              >
                <Brain className="w-4 h-4 mr-2" />
                Seleção Simples
              </Button>
              <Button
                type="button"
                variant={questionnaireMode === 'questionnaire' ? 'default' : 'outline'}
                onClick={() => {
                  setQuestionnaireMode('questionnaire');
                  if (!discQuestionnaire) {
                    setQuestionnaireStep(0);
                  }
                }}
                className={questionnaireMode === 'questionnaire' ? 'bg-purple-600' : ''}
              >
                <ClipboardList className="w-4 h-4 mr-2" />
                Questionário DISC (25 perguntas)
              </Button>
            </div>

            {/* Simple Mode */}
            {questionnaireMode === 'simple' && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Perfil DISC Desejado</Label>
                  <Select 
                    value={form.perfil_cargo?.disc_desejado || 'none'} 
                    onValueChange={(v) => handlePerfilChange('disc_desejado', v === 'none' ? '' : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o perfil..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Qualquer perfil</SelectItem>
                      {DISC_OPTIONS.map(opt => (
                        <SelectItem key={opt} value={opt}>
                          <Badge className="bg-purple-100 text-purple-700">{opt}</Badge>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nível de Experiência</Label>
                  <Select 
                    value={form.perfil_cargo?.nivel_experiencia || 'none'} 
                    onValueChange={(v) => handlePerfilChange('nivel_experiencia', v === 'none' ? '' : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Selecione...</SelectItem>
                      <SelectItem value="junior">Júnior</SelectItem>
                      <SelectItem value="pleno">Pleno</SelectItem>
                      <SelectItem value="senior">Sênior</SelectItem>
                      <SelectItem value="especialista">Especialista</SelectItem>
                      <SelectItem value="gestao">Gestão</SelectItem>
                      <SelectItem value="diretoria">Diretoria</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label>Descrição do Perfil Ideal</Label>
                  <Textarea
                    value={form.perfil_cargo?.descricao_perfil || ''}
                    onChange={(e) => handlePerfilChange('descricao_perfil', e.target.value)}
                    placeholder="Descreva as características comportamentais ideais para este cargo..."
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Questionnaire Mode */}
            {questionnaireMode === 'questionnaire' && (
              <Card className="border-purple-200">
                <CardContent className="p-6">
                  {/* Step 0: Select level and start */}
                  {questionnaireStep === 0 && (
                    <div className="space-y-6">
                      <div className="text-center">
                        <h3 className="text-xl font-semibold text-slate-900">Questionário DISC</h3>
                        <p className="text-slate-600 mt-2">
                          Responda 25 perguntas para definir o perfil DISC ideal para este cargo
                        </p>
                      </div>
                      
                      <div>
                        <Label>Nível Hierárquico do Cargo</Label>
                        <Select 
                          value={form.perfil_cargo?.nivel_experiencia || 'pleno'} 
                          onValueChange={(v) => handlePerfilChange('nivel_experiencia', v)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="operacional">Operacional</SelectItem>
                            <SelectItem value="pleno">Pleno/Analista</SelectItem>
                            <SelectItem value="senior">Sênior/Especialista</SelectItem>
                            <SelectItem value="coordenador">Coordenador</SelectItem>
                            <SelectItem value="gestao">Gerente</SelectItem>
                            <SelectItem value="diretoria">Diretor/C-Level</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <Button
                        type="button"
                        onClick={handleStartQuestionnaire}
                        disabled={loadingQuestionnaire}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                      >
                        {loadingQuestionnaire ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <ClipboardList className="w-4 h-4 mr-2" />
                        )}
                        Iniciar Questionário DISC
                      </Button>
                    </div>
                  )}
                  
                  {/* Questions 1-25 */}
                  {questionnaireStep >= 1 && questionnaireStep <= totalQuestions && discQuestionnaire && (
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">Pergunta {questionnaireStep} de {totalQuestions}</span>
                        <Progress value={(questionnaireStep / totalQuestions) * 100} className="w-48 h-2" />
                      </div>
                      
                      {getCurrentQuestion() && (
                        <div className="p-6 bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg">
                          <h3 className="text-lg font-semibold text-slate-900 mb-4">
                            {getCurrentQuestion().texto}
                          </h3>
                          
                          <RadioGroup
                            value={questionnaireAnswers[questionnaireStep] || ''}
                            onValueChange={(v) => setQuestionnaireAnswers({
                              ...questionnaireAnswers,
                              [questionnaireStep]: v
                            })}
                            className="space-y-3"
                          >
                            {getCurrentQuestion().opcoes?.map((opcao, idx) => (
                              <div key={idx} className="relative">
                                <RadioGroupItem 
                                  value={opcao.perfil}
                                  id={`q${questionnaireStep}-${idx}`}
                                  className="peer sr-only"
                                />
                                <Label
                                  htmlFor={`q${questionnaireStep}-${idx}`}
                                  className="flex items-center p-4 border-2 rounded-lg cursor-pointer bg-white hover:bg-slate-50 peer-data-[state=checked]:border-purple-500 peer-data-[state=checked]:bg-purple-50"
                                >
                                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-200 text-slate-700 font-semibold mr-3">
                                    {String.fromCharCode(65 + idx)}
                                  </span>
                                  <span className="text-slate-700">{opcao.texto}</span>
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                        </div>
                      )}
                      
                      <div className="flex justify-between">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setQuestionnaireStep(Math.max(0, questionnaireStep - 1))}
                        >
                          <ChevronLeft className="w-4 h-4 mr-2" />
                          Anterior
                        </Button>
                        <Button
                          type="button"
                          onClick={() => {
                            if (!questionnaireAnswers[questionnaireStep]) {
                              toast.error('Selecione uma resposta');
                              return;
                            }
                            if (questionnaireStep === totalQuestions) {
                              handleFinishQuestionnaire();
                            } else {
                              setQuestionnaireStep(questionnaireStep + 1);
                            }
                          }}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          {questionnaireStep === totalQuestions ? 'Finalizar' : 'Próxima'}
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Result - Step 26 */}
                  {(questionnaireStep === 26 || calculatedProfile) && (
                    <div className="space-y-6">
                      <div className="text-center">
                        <h3 className="text-xl font-semibold text-slate-900">Perfil DISC Calculado</h3>
                        <p className="text-slate-600 mt-2">Perfil ideal para este cargo:</p>
                      </div>
                      
                      <div className="flex justify-center gap-6">
                        {Object.entries(calculatedProfile?.percentuais || form.perfil_cargo?.disc_percentages || {}).map(([perfil, porcentagem]) => (
                          <div key={perfil} className="text-center">
                            <div className={`w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold mb-2 ${DISC_COLORS[perfil]?.bg} ${DISC_COLORS[perfil]?.text}`}>
                              {perfil}
                            </div>
                            <span className="text-lg font-semibold">{porcentagem}%</span>
                            <p className="text-xs text-slate-500">{DISC_COLORS[perfil]?.name}</p>
                          </div>
                        ))}
                      </div>
                      
                      <div className="p-4 bg-purple-50 rounded-lg text-center">
                        <span className="text-sm text-purple-700">Perfil Predominante:</span>
                        <span className="ml-2 text-xl font-bold text-purple-900">
                          {calculatedProfile?.perfil_predominante || form.perfil_cargo?.disc_desejado}
                        </span>
                      </div>
                      
                      <div className="flex justify-center gap-4">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setQuestionnaireStep(0);
                            setQuestionnaireAnswers({});
                            setCalculatedProfile(null);
                          }}
                        >
                          Refazer Questionário
                        </Button>
                        <Button
                          type="button"
                          onClick={() => setQuestionnaireMode('simple')}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Perfil Salvo ✓
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Overall Score Display */}
            {form.perfil_cargo?.disc_desejado && (
              <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-purple-700 uppercase tracking-wide">Média Geral do Perfil Ideal</span>
                      <p className="text-xs text-purple-600 mt-1">Baseado nas pontuações definidas nos níveis de avaliação</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Star className="w-8 h-8 fill-amber-400 text-amber-400" />
                      <span className="text-4xl font-bold text-purple-900">{calculateOverallMedia()}</span>
                      <span className="text-xl text-purple-600">/5</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Evaluation Levels */}
            {form.perfil_cargo?.niveis?.length > 0 && (
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-base">Níveis de Avaliação - Pontuação Ideal</CardTitle>
                  <CardDescription>
                    Defina a pontuação ideal esperada para cada critério de avaliação deste cargo
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue={form.perfil_cargo.niveis[0]?.nivel} className="w-full">
                    <TabsList className="w-full grid grid-cols-6 h-auto gap-1 p-1 bg-slate-100">
                      {form.perfil_cargo.niveis.map((nivel) => {
                        const Icon = getNivelIcon(nivel.nivel);
                        return (
                          <TabsTrigger 
                            key={nivel.nivel} 
                            value={nivel.nivel}
                            className="flex flex-col py-3 px-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                          >
                            <Icon className="w-5 h-5 mb-1" />
                            <span className="text-xs text-center leading-tight capitalize">{nivel.nivel}</span>
                            <span className="text-sm font-bold text-purple-600">{calculateNivelMedia(nivel)}</span>
                          </TabsTrigger>
                        );
                      })}
                    </TabsList>
                    
                    {form.perfil_cargo.niveis.map((nivel, nivelIndex) => (
                      <TabsContent key={nivel.nivel} value={nivel.nivel} className="mt-4 space-y-4">
                        <div className="p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border-l-4 border-purple-500">
                          <h4 className="font-semibold text-slate-900 text-lg">{nivel.nome}</h4>
                          <p className="text-sm text-slate-600 mt-1">{nivel.descricao}</p>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {nivel.steps?.map((step, stepIndex) => (
                            <div key={stepIndex} className="p-4 border rounded-lg space-y-3 bg-white hover:shadow-sm transition-shadow">
                              <div>
                                <Label className="font-medium text-slate-800">{step.nome}</Label>
                                <p className="text-xs text-slate-500 mt-1">{step.descricao}</p>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-slate-600">Pontuação Ideal:</span>
                                <div className="flex gap-0.5">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <button
                                      key={star}
                                      type="button"
                                      onClick={() => updateStepScore(nivelIndex, stepIndex, star)}
                                      className="p-0.5 hover:scale-110 transition-transform"
                                    >
                                      <Star className={`w-6 h-6 ${
                                        star <= (step.pontuacao_ideal || 0) 
                                          ? 'fill-amber-400 text-amber-400' 
                                          : 'text-slate-300'
                                      }`} />
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </TabsContent>
                    ))}
                  </Tabs>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-start gap-4">
          <Button 
            type="button" 
            variant="outline"
            onClick={() => navigate('/company/job-positions')}
          >
            Cancelar
          </Button>
          <Button 
            type="submit" 
            className="bg-cyan-600 hover:bg-cyan-700"
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isEdit ? 'Atualizar' : 'Criar'} Cargo
          </Button>
        </div>
      </form>

      {/* Import Preview Modal - Editable with Required Field Highlighting */}
      <Dialog open={showImportModal} onOpenChange={(open) => {
        setShowImportModal(open);
        if (!open) {
          setImportPreview(null);
          setImportForm(null);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileUp className="w-5 h-5 text-cyan-600" />
              Dados Extraídos do Documento
            </DialogTitle>
            <DialogDescription>
              Revise e edite os dados mapeados. Campos obrigatórios em vermelho devem ser preenchidos.
            </DialogDescription>
          </DialogHeader>

          {importPreview?.raw_text ? (
            <div className="space-y-4 py-4">
              <Label>Texto Extraído (copie manualmente)</Label>
              <Textarea
                value={importPreview.raw_text}
                readOnly
                className="h-64 font-mono text-xs"
              />
            </div>
          ) : importForm && (
            <div className="space-y-6 py-4">
              {/* Required fields warning */}
              {getImportMissingFields().length > 0 && (
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">Campos obrigatórios não identificados</p>
                    <p className="text-sm text-red-600 mt-1">
                      Preencha os campos destacados em vermelho antes de aplicar os dados ao formulário.
                    </p>
                  </div>
                </div>
              )}

              {/* Identificação */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-cyan-600" />
                  Identificação
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Título - OBRIGATÓRIO */}
                  <div className="md:col-span-2">
                    <Label className={`flex items-center gap-1 ${!importForm.titulo?.trim() ? 'text-red-600' : ''}`}>
                      Título do Cargo *
                      {!importForm.titulo?.trim() && (
                        <span className="text-xs font-normal">(obrigatório)</span>
                      )}
                    </Label>
                    <Input
                      value={importForm.titulo}
                      onChange={(e) => setImportForm({ ...importForm, titulo: e.target.value })}
                      placeholder="Ex: Analista de Recursos Humanos"
                      className={!importForm.titulo?.trim() ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-200' : ''}
                    />
                  </div>

                  <div>
                    <Label>Código do Cargo</Label>
                    <Input
                      value={importForm.codigo_cargo}
                      onChange={(e) => setImportForm({ ...importForm, codigo_cargo: e.target.value })}
                      placeholder="Ex: RH-001"
                    />
                  </div>

                  <div>
                    <Label>Código CBO</Label>
                    <Input
                      value={importForm.codigo_cbo}
                      onChange={(e) => setImportForm({ ...importForm, codigo_cbo: e.target.value })}
                      placeholder="Ex: 2524-05"
                    />
                  </div>

                  <div>
                    <Label>Jornada Diária</Label>
                    <Input
                      value={importForm.jornada_diaria}
                      onChange={(e) => setImportForm({ ...importForm, jornada_diaria: e.target.value })}
                      placeholder="Ex: 08:00"
                    />
                  </div>

                  <div>
                    <Label>Jornada Semanal</Label>
                    <Input
                      value={importForm.jornada_semanal}
                      onChange={(e) => setImportForm({ ...importForm, jornada_semanal: e.target.value })}
                      placeholder="Ex: 44h"
                    />
                  </div>
                </div>
              </div>

              {/* Missão - OBRIGATÓRIO */}
              <div className="space-y-2">
                <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Target className="w-4 h-4 text-cyan-600" />
                  Missão do Cargo
                </h4>
                <Label className={`flex items-center gap-1 ${!importForm.descricao_sumaria?.trim() ? 'text-red-600' : ''}`}>
                  Descrição Sumária *
                  {!importForm.descricao_sumaria?.trim() && (
                    <span className="text-xs font-normal">(obrigatório)</span>
                  )}
                </Label>
                <Textarea
                  value={importForm.descricao_sumaria}
                  onChange={(e) => setImportForm({ ...importForm, descricao_sumaria: e.target.value })}
                  placeholder="Descreva a missão e objetivo principal do cargo..."
                  rows={3}
                  className={!importForm.descricao_sumaria?.trim() ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-200' : ''}
                />
              </div>

              {/* Pré-requisitos */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-cyan-600" />
                  Pré-requisitos
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Escolaridade Mínima</Label>
                    <Input
                      value={importForm.escolaridade}
                      onChange={(e) => setImportForm({ ...importForm, escolaridade: e.target.value })}
                      placeholder="Ex: Ensino Superior Completo"
                    />
                  </div>

                  <div>
                    <Label>Escolaridade Desejável</Label>
                    <Input
                      value={importForm.escolaridade_desejavel}
                      onChange={(e) => setImportForm({ ...importForm, escolaridade_desejavel: e.target.value })}
                      placeholder="Ex: Pós-graduação em RH"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label>Experiência</Label>
                    <Input
                      value={importForm.experiencia}
                      onChange={(e) => setImportForm({ ...importForm, experiencia: e.target.value })}
                      placeholder="Ex: 2 anos em gestão de pessoas"
                    />
                  </div>

                  <div>
                    <Label>Informática Básica</Label>
                    <Input
                      value={importForm.informatica}
                      onChange={(e) => setImportForm({ ...importForm, informatica: e.target.value })}
                      placeholder="Ex: Pacote Office"
                    />
                  </div>

                  <div>
                    <Label>Informática Avançada</Label>
                    <Input
                      value={importForm.informatica_avancado}
                      onChange={(e) => setImportForm({ ...importForm, informatica_avancado: e.target.value })}
                      placeholder="Ex: SAP, Power BI"
                    />
                  </div>
                </div>
              </div>

              {/* Responsabilidades - Preview only */}
              {importForm.responsabilidades?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                    <ListChecks className="w-4 h-4 text-cyan-600" />
                    Responsabilidades Identificadas
                  </h4>
                  <div className="p-3 bg-slate-50 rounded-lg space-y-2">
                    {importForm.responsabilidades.map((r, idx) => (
                      <div key={idx}>
                        <span className="font-medium text-slate-700">{r.categoria}:</span>
                        <ul className="list-disc ml-6 text-sm text-slate-600">
                          {r.itens?.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Habilidades - Preview only */}
              {importForm.habilidades_competencias?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-cyan-600" />
                    Habilidades e Competências
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {importForm.habilidades_competencias.map((h, idx) => (
                      <Badge key={idx} variant="outline">{h}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Conhecimentos - Preview only */}
              {importForm.conhecimentos_especificos?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-cyan-600" />
                    Conhecimentos Específicos
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {importForm.conhecimentos_especificos.map((c, idx) => (
                      <Badge key={idx} variant="outline">{c}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Treinamentos - Preview only */}
              {importForm.treinamentos_corporativos?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-amber-600" />
                    Treinamentos Corporativos
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {importForm.treinamentos_corporativos.map((t, idx) => (
                      <Badge key={idx} className="bg-amber-100 text-amber-700">{t}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Linha de Carreira */}
              <div className="space-y-2">
                <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-cyan-600" />
                  Linha de Carreira
                </h4>
                <Input
                  value={importForm.linha_carreira}
                  onChange={(e) => setImportForm({ ...importForm, linha_carreira: e.target.value })}
                  placeholder="Ex: Coordenador de RH → Gerente de RH"
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex items-center justify-between gap-4 border-t pt-4">
            <div className="text-sm text-slate-500">
              {importForm && getImportMissingFields().length > 0 && (
                <span className="text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  {getImportMissingFields().length} campo(s) obrigatório(s) pendente(s)
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setShowImportModal(false); setImportPreview(null); setImportForm(null); }}>
                Cancelar
              </Button>
              {importForm && (
                <Button 
                  onClick={applyImportedData} 
                  className={getImportMissingFields().length > 0 
                    ? 'bg-slate-400 cursor-not-allowed' 
                    : 'bg-cyan-600 hover:bg-cyan-700'
                  }
                  disabled={getImportMissingFields().length > 0}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Aplicar ao Formulário
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Generation Modal */}
      <Dialog open={showAIModal} onOpenChange={setShowAIModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-purple-600" />
              Gerar Descrição com IA
            </DialogTitle>
            <DialogDescription>
              Informe os dados básicos e a IA gerará uma descrição completa do cargo
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="ai_titulo">Título do Cargo *</Label>
              <Input
                id="ai_titulo"
                value={aiForm.titulo}
                onChange={(e) => setAIForm({ ...aiForm, titulo: e.target.value })}
                placeholder="Ex: Analista de Recursos Humanos"
              />
            </div>
            <div>
              <Label htmlFor="ai_cbo">Código CBO (opcional)</Label>
              <Input
                id="ai_cbo"
                value={aiForm.codigo_cbo}
                onChange={(e) => setAIForm({ ...aiForm, codigo_cbo: e.target.value })}
                placeholder="Ex: 2524-05"
              />
              <p className="text-xs text-slate-500 mt-1">
                Consulte em: <a href="https://www.ocupacoes.com.br/" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">ocupacoes.com.br</a>
              </p>
            </div>
            <div>
              <Label htmlFor="ai_setor">Setor/Departamento</Label>
              <Input
                id="ai_setor"
                value={aiForm.setor}
                onChange={(e) => setAIForm({ ...aiForm, setor: e.target.value })}
                placeholder="Ex: Recursos Humanos, Financeiro, TI"
              />
            </div>
            <div>
              <Label htmlFor="ai_nivel">Nível Hierárquico</Label>
              <Select
                value={aiForm.nivel_hierarquico}
                onValueChange={(v) => setAIForm({ ...aiForm, nivel_hierarquico: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o nível" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="estagiario">Estagiário</SelectItem>
                  <SelectItem value="auxiliar">Auxiliar</SelectItem>
                  <SelectItem value="assistente">Assistente</SelectItem>
                  <SelectItem value="analista_jr">Analista Júnior</SelectItem>
                  <SelectItem value="analista_pl">Analista Pleno</SelectItem>
                  <SelectItem value="analista_sr">Analista Sênior</SelectItem>
                  <SelectItem value="especialista">Especialista</SelectItem>
                  <SelectItem value="coordenador">Coordenador</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="gerente">Gerente</SelectItem>
                  <SelectItem value="diretor">Diretor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="ai_descricao_previa">Descrição Prévia da Atividade</Label>
              <Textarea
                id="ai_descricao_previa"
                value={aiForm.descricao_previa}
                onChange={(e) => setAIForm({ ...aiForm, descricao_previa: e.target.value })}
                placeholder="Descreva brevemente as principais atividades e responsabilidades do cargo para auxiliar a IA a gerar uma descrição mais precisa e próxima da realidade..."
                rows={4}
              />
              <p className="text-xs text-slate-500 mt-1">
                Esta descrição prévia ajuda a IA a entender melhor o contexto e gerar uma descrição mais adequada às suas necessidades.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAIModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAIGenerate} 
              disabled={aiLoading || !aiForm.titulo.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {aiLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Gerar Descrição
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
